import { useMutation, useQuery } from "@tanstack/react-query";
import { baseApi } from "./api";

// TypeScript interfaces based on the actual API response
interface Contact {
  id: number;
  firstName: string;
  lastName: string;
  company: string;
}

interface ContactPhone {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  contactId: number;
  phone: string;
  contact: Contact;
}

interface Secretary {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  email: string;
  firstName: string;
  lastName: string;
  agentId: string;
}

interface Business {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  idMongo: string;
  cityId: number | null;
  name: string;
  activityType: string | null;
  companyName: string | null;
  nationalId: string | null;
  landLine: string | null;
  economicCode: string | null;
  postalCode: string | null;
  address: string | null;
}

interface Line {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  phone: string;
  available: boolean;
}

interface BusinessLine {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  idMongo: string;
  businessId: number;
  lineId: number;
  name: string | null;
  recordingCall: boolean;
  totalDurationCall: number;
  expiredAt: string | null;
  blockAt: string | null;
  status: string;
  business: Business;
  line: Line;
}

interface FormData {
  email?: string;
  address?: string;
  phoneNumber?: string;
  company?: string;
}

export interface Activity {
  id: number;
  createdAt: string;
  updatedAt: string;
  amiCallId: string;
  secretaryId: number;
  contactPhoneId: number;
  businessLineId: number;
  durationCall: number;
  wrapUpTime: number;
  formData: FormData;
  isRead: boolean;
  answerAt: string;
  secretary: Secretary;
  businessLine: BusinessLine;
  contactPhone: ContactPhone;
}

export interface CallRecordingResponse {
  link: string;
  timestamp: number;
}

const ACTIVITIES_ROUTES = {
  GET_ACTIVITIES: "/activities/",
  GET_ACTIVITY_CALL_RECORDING: "/activities/:id/call-recording",
  PUT_ACTIVITY_READ: "/activities/:id/read",
};

export const activitiesApiHooks = {
  usePutActivityRead: (options?: any) => {
    return useMutation<Activity, Error, string>({
      mutationFn: async (activityId: string) => {
        const url = ACTIVITIES_ROUTES.PUT_ACTIVITY_READ.replace(
          ":id",
          activityId
        );
        const response = await baseApi.put(url);
        return response.data;
      },
      ...options,
    });
  },

  useGetActivities: (business_line_id: string[] = [], contact_id?: string) => {
    return useQuery<Activity[]>({
      queryKey: ["activities", business_line_id, contact_id],
      queryFn: async () => {
        let url = ACTIVITIES_ROUTES.GET_ACTIVITIES;
        const params = new URLSearchParams();

        if (business_line_id.length > 0) {
          business_line_id.forEach((id) => {
            params.append("business_line_id", id);
          });
        }
        if (contact_id) {
          params.append("contact_id", contact_id);
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const response = await baseApi.get(url);
        return response.data.data;
      },
    });
  },

  useGetActivityCallRecording: (id: string) => {
    return useQuery<CallRecordingResponse>({
      queryKey: ["activityCallRecording", id],
      queryFn: async () => {
        const url = ACTIVITIES_ROUTES.GET_ACTIVITY_CALL_RECORDING.replace(
          ":id",
          id
        );
        const response = await baseApi.get(url);
        // Add timestamp when URL was fetched
        return {
          ...response.data,
          timestamp: Date.now(),
        };
      },
      enabled: !!id, // Only run query when id is truthy
      // Cache for 50 minutes (3000 seconds) to be safe before 1-hour expiration
      staleTime: 50 * 60 * 1000, // 50 minutes in milliseconds
      gcTime: 50 * 60 * 1000, // 50 minutes in milliseconds
      // Don't refetch on window focus or reconnect
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // Only refetch if data is stale
      refetchOnMount: false,
      // Enable retry for network errors
      retry: (failureCount, error: any) => {
        // Don't retry if it's a 4xx error (client error)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    });
  },
};

export default activitiesApiHooks;
